import React from 'react';
import { useRouter } from 'next/router';
import classnames from 'classnames';

import {
  RecipeCarousel, RecipeIngredientsModal, RecipesEmptyState, RecipesLandingPageHero
} from 'components/recipe';
import { Breadcrumbs } from 'components/ui';
import { APP_ROUTES } from 'constants/routes';
import { isArrayEmpty } from 'helpers/ArrayHelpers';
import { useIsOpen } from 'hooks/useIsOpen';
import RecipesSearchBox from 'templates/Recipes/RecipesSearchBox/RecipesSearchBox';

import type {
  IRecipeCard,
  IRecipesLandingPage,
  IRecipesLandingPageSlot,
} from 'types';

import {
  RECIPES_LANDING_PAGE_DATA_SLOT_TYPES,
  RECIPES_LANDING_PAGE_SLOT_TYPES,
} from 'types';

import styles from './RecipesLandingPageTemplate.module.scss';

// Constants

const BREADCRUMBS = [
  {
    href: APP_ROUTES.HOME,
    name: 'NAVIGATION.HOME',
    translate: true
  },
  {
    href: APP_ROUTES.RECIPES_LIST,
    name: 'NAVIGATION.BLOG.RECIPES.DEFAULT',
    translate: true
  },
];

// Props

interface Props {
  className?: string,
  data: IRecipesLandingPage
}

// Component

const RecipesLandingPageTemplate = (props: Props) => {

  const {
    className,
    data
  } = props;

  // Props

  const {
    hero,
    slots
  } = data;

  // Hooks

  const [selectedRecipeCard, setSelectedRecipeCard] = React.useState<IRecipeCard>();

  const router = useRouter();

  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useIsOpen();

  // Handlers

  const handleRecipeViewIngredientsClick = (recipeCard: IRecipeCard, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedRecipeCard(recipeCard);
    openModal();
  };

  const handleSearch = (newQuery: string | null) => {
    if (newQuery?.trim()) {
      router.push(`${APP_ROUTES.RECIPES_LIST_FILTERED}/search/${encodeURIComponent(newQuery)}`);
    }
  };

  // Render
  const withSearchBox = true;

  return (
    <>

      {/* Root */}
      <div className={classnames(styles.root, className)}>

        {/* Breadcrumbs */}
        <Breadcrumbs
          className={styles.breadcrumbs}
          items={BREADCRUMBS}
        />

        {/* Hero */}
        <RecipesLandingPageHero
          className={styles.hero}
          hero={hero}
        />

        {/* Content */}
        <div className={styles.content}>

          {/* Search */}
          {
            withSearchBox && (
              <RecipesSearchBox
                className={styles.search}
                onSearch={handleSearch}
              />
            )
          }

          {/* Slots */}
          {
            isArrayEmpty(slots)
              ? (
                <RecipesEmptyState className={styles.emptyState} />
              ) : (
                <div className={styles.slots}>
                  {
                    slots.map((slot, index) => (
                      slot
                        ? (
                          <RecipesLandingPageSlot
                            key={`recipes-landing-page-slot-${index}`}
                            className={styles.slot}
                            slot={slot}
                            additionalParams={{
                              onRecipeViewIngredientsClick: handleRecipeViewIngredientsClick
                            }}
                          />
                        ) : null
                    ))
                  }
                </div>
              )
          }

        </div>
      </div>

      {/* Modal */}
      <RecipeIngredientsModal
        isOpen={isModalOpen}
        //
        title={selectedRecipeCard?.title}
        recipeId={selectedRecipeCard?.id}
        recipeSlug={selectedRecipeCard?.slug}
        //
        onClose={() => {
          closeModal();
        }}
        onAfterClose={() => {
          setSelectedRecipeCard(undefined);
        }}
      />

    </>
  );
};

// Component

// --- Recipes Landing Page Slot

interface RecipesLandingPageSlotProps {
  className?: string,
  slot: IRecipesLandingPageSlot,
  additionalParams?: {
    onRecipeClick?: (recipe: IRecipeCard, e: React.MouseEvent<HTMLAnchorElement>) => void,
    onRecipeViewIngredientsClick?: (recipe: IRecipeCard, e: React.MouseEvent<HTMLButtonElement>) => void
  }
}

const RecipesLandingPageSlot = (props: RecipesLandingPageSlotProps) => {
  const {
    className,
    slot,
    additionalParams,
  } = props;

  const {
    slotClassName,
    component: Component,
    componentProps,
  } = getSlotComponent(slot, additionalParams) || {};

  if (!Component) {
    return null;
  }

  return (
    <div
      className={classnames(
        styles.slot,
        slotClassName,
        className
      )}
    >
      {/* @ts-ignore */}
      <Component
        {...componentProps}
      />
    </div>
  );
};

// Helpers

// --- Get Slot Component

interface GetSlotComponentAdditionalParams {
  onRecipeClick?: (recipe: IRecipeCard, e: React.MouseEvent<HTMLAnchorElement>) => void,
  onRecipeViewIngredientsClick?: (recipe: IRecipeCard, e: React.MouseEvent<HTMLButtonElement>) => void
}

const getSlotComponent = (slot: IRecipesLandingPageSlot, additionalParams?: GetSlotComponentAdditionalParams) => {
  const {
    type,
    data: {
      title,
      link,
      recipes,
    }
  } = slot;

  const {
    onRecipeClick,
    onRecipeViewIngredientsClick
  } = additionalParams || {};

  switch (type) {
    case RECIPES_LANDING_PAGE_SLOT_TYPES[RECIPES_LANDING_PAGE_DATA_SLOT_TYPES.RECIPES_CAROUSEL]:
      return {
        slotClassName: styles.recipeCarouselSlot,
        component: RecipeCarousel,
        componentProps: {
        //
          className: styles.recipeCarousel,
          titleClassName: styles.recipeCarouselTitle,
          sliderClassName: styles.recipeCarouselSlider,
          navigationButtonClassName: styles.recipeCarouselNavBtn,
          //
          title,
          link,
          recipes,
          //
          breakpoints: RECIPE_CAROUSEL_BREAKPOINTS,
          //
          onRecipeClick,
          onRecipeViewIngredientsClick,
        }
      };

    default:
      return null;
  }
};

// Constants

const RECIPE_CAROUSEL_BREAKPOINTS = {
  360: {
    slidesPerView: 1.25,
    slidesPerGroup: 1,
    spaceBetween: 12,
  },
  420: {
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    spaceBetween: 16,
  },
  600: {
    slidesPerView: 2.25,
    slidesPerGroup: 2,
    spaceBetween: 16,
  },
  780: {
    slidesPerView: 2.5,
    slidesPerGroup: 2,
    spaceBetween: 16,
  },
  960: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 24,
  },
  1140: {
    slidesPerView: 4,
    slidesPerGroup: 4,
    spaceBetween: 24,
  },
};

// Export

export default RecipesLandingPageTemplate;
